import { FC } from 'react';

import { BrandMenu } from './components/BrandMenu';
import { type Eula } from './components/BrandMenu/components/HelpSubmenu';
import { MainNavTabs } from './components/MainNavTabs';
import { ModeSwitch } from './components/ModeSwitch';
import { RealmSelect } from './components/RealmSelect';
import SubHeader from './components/SubHeader';

import { useHeaderContext } from '~/components/HeaderProvider';

interface HeaderProps {
  byId: Record<string, { id: string; name: string; companyName: string }>;
  allIds: string[];
  activeRealm: { realm: string };
  canSelectRealm: boolean;
  currentPath: string;
  containPaths: (paths: string[]) => boolean;
  containScopes: (roles: string[]) => boolean;
  pathNameRuntime: () => string | undefined;
  getEnvriomentByFeatureFlag: (environments: string[]) => string | undefined;
  environments: string[];
  goToAdmin: () => void;
  goToSettings: () => void;
  goToConnectivity: () => void;
  canSeeConnectivity: boolean;
  unreadCount?: number;
  onToggle: () => void;
  eula?: Eula | null;
  openEula: () => void;
  openSupportAccess: () => void;
  enabledSupportAccess?: boolean;
  userName?: string;
  isMainRealm: boolean;
  setModalProfile: (open: boolean) => void;
  setModalNewPassword: (open: boolean) => void;
  setModalTwoFactor: (open: boolean) => void;
  onSignOut: () => void;
  goToManualMode: () => void;
  goToDigitalWorkerMode: () => void;
  isDigitalWorkerNavigable: boolean;
  modeSwitchView: 'Build' | 'Run' | 'Monitor' | null;
  canUseDigitalWorker: boolean;
  links: any[];
  environmentSelectRouteWhiteList?: string[];
}

export const Header: FC<HeaderProps> = ({
  byId = {},
  allIds = [],
  activeRealm,
  canSelectRealm,
  currentPath,
  containPaths,
  containScopes,
  pathNameRuntime,
  getEnvriomentByFeatureFlag,
  environments,
  goToAdmin,
  goToSettings,
  goToConnectivity,
  canSeeConnectivity,
  unreadCount,
  onToggle,
  eula,
  openEula,
  openSupportAccess,
  enabledSupportAccess,
  userName,
  isMainRealm,
  setModalProfile,
  setModalNewPassword,
  setModalTwoFactor,
  onSignOut,
  goToManualMode,
  goToDigitalWorkerMode,
  isDigitalWorkerNavigable,
  modeSwitchView,
  canUseDigitalWorker,
  links,
  environmentSelectRouteWhiteList
}) => {
  const { configActionButton } = useHeaderContext();

  return (
    <div className='w-full bg-surface'>
      <div className='relative flex items-center justify-between px-7 pb-4 pt-5'>
        <div className='flex items-center gap-9'>
          <BrandMenu
            goToAdmin={goToAdmin}
            goToSettings={goToSettings}
            goToConnectivity={goToConnectivity}
            showConnectivity={canSeeConnectivity}
            unreadCount={unreadCount}
            onToggleChat={onToggle}
            eula={eula}
            openEula={openEula}
            openSupportAccess={openSupportAccess}
            enabledSupportAccess={enabledSupportAccess}
            userName={userName}
            isMainRealm={isMainRealm}
            setModalProfile={setModalProfile}
            setModalNewPassword={setModalNewPassword}
            setModalTwoFactor={setModalTwoFactor}
            onSignOut={onSignOut}
          />
          <RealmSelect
            activeRealm={activeRealm.realm}
            allIds={allIds}
            byId={byId}
            canSelect={canSelectRealm}
            currentPath={currentPath}
          />
        </div>
        <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'>
          <MainNavTabs
            realm={activeRealm.realm}
            environments={environments}
            containPaths={containPaths}
            containScopes={containScopes}
            pathNameRuntime={pathNameRuntime}
            getEnvriomentByFeatureFlag={getEnvriomentByFeatureFlag}
          />
        </div>
        <ModeSwitch
          goToManualMode={goToManualMode}
          goToDigitalWorkerMode={goToDigitalWorkerMode}
          isDigitalWorkerNavigable={isDigitalWorkerNavigable}
          view={modeSwitchView}
          isDisabled={!canUseDigitalWorker}
        />
      </div>
      <SubHeader
        links={links}
        activeRealm={activeRealm}
        configActionButton={configActionButton}
        environments={environments}
        environmentSelectRouteWhiteList={environmentSelectRouteWhiteList}
      />
    </div>
  );
};
